import type { SiteConditionsContent } from '@gg/shared/i18n';
import { fill } from '@/components/site/siteHelpers';

/**
 * "Puis-je participer ?" checker on /conditions: indicative answer from the sporting rules V7
 * (ages, documents, minors' formalities). Ported from the design (Conditions.dc.html).
 * The event list is the 2027 edition's editorial content, like the copy itself.
 */
export type SiteEventId = keyof SiteConditionsContent['ev'];
export type SiteDisc = keyof SiteConditionsContent['disc'];
export type LicenceStatus = 'lic' | 'non';

export interface SiteEvent {
  id: SiteEventId;
  disc: SiteDisc;
  min: number;
  open: boolean;
  /** Minimum age still to be confirmed. */
  ageToConfirm?: boolean;
}

export const SITE_EVENTS: readonly SiteEvent[] = [
  { id: 'r5', disc: 'running', min: 16, open: true },
  { id: 'r10', disc: 'running', min: 16, open: true },
  { id: 'r20', disc: 'running', min: 18, open: true },
  { id: 'c35', disc: 'c35', min: 16, open: true },
  { id: 'c70', disc: 'cycling', min: 18, open: true },
  { id: 'c100', disc: 'cycling', min: 18, open: true },
  { id: 'rr12', disc: 'rr', min: 16, open: true },
  { id: 'rr24', disc: 'rr', min: 18, open: true },
  { id: 'e12', disc: 'e12', min: 18, open: true },
];

export const isPairEvent = (id: string) => SITE_EVENTS.find((e) => e.id === id)?.disc === 'rr';

export interface CheckerState {
  ev: string;
  age: string;
  status: LicenceStatus | '';
  age2: string;
  status2: LicenceStatus | '';
}

export const emptyChecker: CheckerState = { ev: '', age: '', status: '', age2: '', status2: '' };

export interface PairMember {
  who: string;
  statusLabel: string;
  docs: string[];
  /** No FFTRI licence: link to the Licence Expérience. */
  licExp: boolean;
  minor: string[];
}

export interface CheckerResult {
  evName: string;
  eligible: boolean;
  isPair: boolean;
  headline: string;
  ageMsg: string;
  alts: string[];
  docs: string[];
  /** Adult on the 35K ride: no specific health document. */
  noDocsMsg?: string;
  showPps: boolean;
  /** Timed cycling races: link to the FFC health module. */
  showFfcHealth: boolean;
  minor: string[];
  members: PairMember[];
  notes: string[];
  canRegister: boolean;
  soon: boolean;
}

const parseAge = (s: string) => {
  const n = Number.parseInt(s, 10);
  return Number.isNaN(n) || n < 1 || n > 120 ? null : n;
};

/** Null until every question is answered (both team members for Run & Ride). */
export function computeEligibility(
  t: SiteConditionsContent,
  s: CheckerState,
): CheckerResult | null {
  const e = SITE_EVENTS.find((x) => x.id === s.ev);
  const age = parseAge(s.age);
  if (!e || age === null || !s.status) return null;
  if (e.disc === 'rr') return computePair(t, e, age, s);

  const c = t.check;
  const eligible = age >= e.min;
  const res: CheckerResult = {
    evName: t.ev[e.id],
    eligible,
    isPair: false,
    headline: eligible ? c.okHead : c.koHead,
    ageMsg: eligible
      ? c.okAge + (e.ageToConfirm ? ` ${c.confirmAge}` : '')
      : fill(c.koAge, { n: e.min }),
    alts: [],
    docs: [],
    showPps: false,
    showFfcHealth: false,
    minor: [],
    members: [],
    notes: [],
    canRegister: eligible && e.open,
    soon: eligible && !e.open,
  };
  if (!eligible) {
    res.alts = SITE_EVENTS.filter((x) => age >= x.min).map((x) => t.ev[x.id]);
    if (res.alts.length === 0) res.alts = [c.none];
    return res;
  }
  const minorAge = age < 18;
  const lic = s.status === 'lic';
  const { d, n } = c;
  if (e.disc === 'running') {
    if (minorAge) {
      res.docs = [...d.runMinor];
      res.minor = [c.m.consent];
    } else {
      res.docs = [...(lic ? d.runLic : d.runNon)];
      res.showPps = true;
    }
  } else if (e.disc === 'c35') {
    res.notes.push(n.untimed);
    if (minorAge) {
      res.docs = [...d.c35Minor];
      res.minor = [c.m.consent];
    } else {
      res.noDocsMsg = c.noDocs35;
    }
  } else if (e.disc === 'cycling') {
    res.docs = [...d.cyc];
    res.showFfcHealth = true;
    res.notes.push(n.ppsNote, n.noEbike);
  } else if (e.disc === 'e12') {
    res.docs = [...d.e12];
    res.notes.push(n.e12Team, n.e12Rules);
  }
  res.notes.push(n.noStart, n.id);
  return res;
}

function computePair(
  t: SiteConditionsContent,
  e: SiteEvent,
  age: number,
  s: CheckerState,
): CheckerResult | null {
  const age2 = parseAge(s.age2);
  if (age2 === null || !s.status2) return null;
  const c = t.check;
  const P = c.pair;
  const okYou = age >= e.min;
  const okMate = age2 >= e.min;
  const eligible = okYou && okMate;
  const who = !okYou && !okMate ? P.whoBoth : !okYou ? P.whoYou : P.whoMate;
  const res: CheckerResult = {
    evName: t.ev[e.id],
    eligible,
    isPair: true,
    headline: eligible ? P.okHead : P.koHead,
    ageMsg: eligible ? fill(P.okAge, { n: e.min }) : fill(P.koAge, { n: e.min, who }),
    alts: [],
    docs: [],
    showPps: false,
    showFfcHealth: false,
    minor: [],
    members: [],
    notes: [],
    canRegister: eligible && e.open,
    soon: eligible && !e.open,
  };
  if (!eligible) {
    const young = Math.min(age, age2);
    res.alts = SITE_EVENTS.filter((x) => x.disc === 'rr' && young >= x.min).map((x) => t.ev[x.id]);
    return res;
  }
  const member = (label: string, a: number, st: LicenceStatus): PairMember => {
    const docs = [...(st === 'lic' ? c.d.rrLic : c.d.rrNon)];
    if (a >= 18) docs.push(c.d.rrHealthAdult);
    return {
      who: label,
      statusLabel: st === 'lic' ? P.lic : P.non,
      docs,
      licExp: st === 'non',
      minor: a < 18 ? [c.m.consent, c.m.rrHealth] : [],
    };
  };
  res.members = [member(P.you, age, s.status as LicenceStatus), member(P.mate, age2, s.status2)];
  res.notes.push(c.n.rrFrame, c.n.rrBike, c.n.noStart, c.n.id);
  return res;
}
